import { PDFDocument, PDFName, PDFDict, PDFArray, PDFStream, PDFRawStream } from "pdf-lib";
import { stampOutputMetadata, stemOfPdfName } from "./pdfMetadata";
import { findImages, recompressImages, recompressSmart, recompressToFit, BALANCED_140, BALANCED_SETTING, SMALLEST_SETTING, CompressStopped, type ImageCodec } from "./imageRecompress";
import { compressPagesInLevelsWith, emptyPages, type LevelCheckpoint, type PageEnv } from "./pageLevels";
import { formatFileSize } from "@/lib/formatFileSize";
import { plural } from "@/lib/plural";

/**
 * The whole Compress PDF ladder (tidy, blank pages, pictures inside, pages as pictures, greyscale, black and white),
 * written once for both the site and the API. Everything environment-specific comes in through LadderEnv: how to open
 * a PDF with pdf.js, make a canvas, write a JPEG and load the JBIG2 encoder. The site's binding is targetCompression.ts
 * (browser), the API's is utility-api core/pdf/ladder.ts (Node).
 */

export interface LadderEnv extends PageEnv {
  /** Re-encodes pictures that sit inside the PDF. */
  codec: ImageCodec;
  /** pdf.js's OPS table, used to tell which pages draw nothing. */
  ops: Record<string, number>;
}

export type { LevelCheckpoint };

/** auto = the default: aim for about half the size (structure first, then the gentlest picture shrink that fits). */
export type CompressMode = "auto" | "medium" | "strong" | "target" | "balanced" | "aggressive" | "extreme" | "size";

/** How far the step-by-step modes may go. Each one includes everything before it.
 *  balanced: tidy, empty blank pages, shrink pictures inside (text stays selectable).
 *  aggressive: also turn pages into pictures, finer and finer (text is no longer selectable).
 *  extreme: also greyscale at low resolution, then pure black and white (colour is lost). */
export type CompressLimit = "balanced" | "aggressive" | "extreme";

/** Why a compression left the file as it was. */
export type CompressUnchangedReason =
  | "no-gain" // the best we could do saved under 1%
  | "no-images" // balanced/smallest shrink pictures and this PDF has none worth shrinking
  | "unsupported-pictures" // it has big pictures, but of a kind this tool cannot shrink
  | "under-target"; // the file already fits the target

export type CompressResult =
  | {
      status: "ok";
      bytes: Uint8Array;
      size: number;
      /** What was done: tidied structure only, shrank pictures inside, or turned whole pages into pictures. */
      method: "structure" | "images" | "pictures";
      metTarget?: boolean;
      targetBytes?: number;
      imagesShrunk: number;
      imageCount: number;
      /** For target mode: pictures alone could not reach the target (so "pages to pictures" could be offered). */
      pictureFallbackWouldHelp?: boolean;
      /** Pages-to-pictures was tried and was not smaller than what pictures-inside gave, so it was not used. */
      pictureFallbackTried?: boolean;
      /** For "pictures": how many pages became pictures (the ones worth it) and how many were left as they were. */
      pagesConverted?: number;
      pagesKept?: number;
      /** Aggressive / Extreme ended before every level they promise was offered, and why (see stopNoteSentence). */
      stopNote?: StopNote;
    }
  | { status: "unchanged"; reason: CompressUnchangedReason; imageCount: number; pictureFallbackWouldHelp?: boolean; pictureFallbackTried?: boolean };

/** Why Aggressive or Extreme did not offer every level they describe.
 *  already-small: the pictures inside were already fitted to about 2% of the original size, so the heavier levels were not run (they can be, on request).
 *  no-smaller: the heavier levels were run and none was smaller than the best level already offered. */
export interface StopNote {
  kind: "already-small" | "no-smaller";
  /** The level number of the file offered as the result. */
  level: number;
  /** How much smaller than the original that file is, in whole percent. */
  savedPercent: number;
  /** The mode's limit, which decides what the skipped levels would have been. */
  limit: CompressLimit;
}

/** The one plain sentence shown when a mode stops early (shared by the site and the API). */
export function stopNoteSentence(note: StopNote): string {
  const steps = note.limit === "extreme"
    ? "turn pages into pictures, then grey, then black and white"
    : "turn pages into pictures";
  if (note.kind === "already-small") {
    return `Stopped at level ${note.level}: the file is already ${note.savedPercent}% smaller, and the next levels would ${steps}, so text and colour would be lost.`;
  }
  const tried = note.limit === "extreme" ? "The heavier levels (pages as pictures, grey, black and white)" : "The heavier levels (pages as pictures)";
  return `${tried} were tried and none came out smaller than level ${note.level}, so level ${note.level} is the smallest this mode can make from this file.`;
}

/** A saving smaller than this is not worth a different file. */
const MIN_SAVING = 0.01;
/** Searching aims a little under the target so file managers that count 1 KB as 1000 bytes agree. */
const TARGET_MARGIN = 0.985;


export const TIDY_LEVEL = { name: "Tidy the file", detail: "Repacks the file's internal structure. Nothing you can see changes." };
export const BLANKS_LEVEL = { name: "Blank pages emptied", detail: "Pages with nothing on them are stored as empty pages. Nothing you can see changes." };
export const PICS_QUICK_LEVEL = { name: "Pictures inside made smaller (quick)", detail: "Photos and scans stored inside the PDF are re-saved once at a balanced quality. Text stays text." };
export const PICS_FIT_LEVEL = { name: "Pictures inside fitted to the size", detail: "Each picture is measured and shrunk only as far as it can go without visibly suffering. Text stays text." };


/**
 * Pages that draw nothing (no text, picture, fill or stroke, no annotations), found without rendering anything. A cheap
 * look at the file's structure rules out most pages first, so scanned books are not decoded just to be looked at.
 */
export async function findBlankPagesIn(original: Uint8Array, env: LadderEnv): Promise<number[]> {
  const lib = await PDFDocument.load(original, { ignoreEncryption: true, updateMetadata: false, throwOnInvalidObject: false });
  const candidates = new Set<number>();
  lib.getPages().forEach((p, i) => {
    const node = p.node;
    if (node.lookup(PDFName.of("Annots"))) return;
    const res = node.lookup(PDFName.of("Resources"));
    if (res instanceof PDFDict) {
      const xo = res.lookup(PDFName.of("XObject"));
      if (xo instanceof PDFDict && xo.keys().length) return;
    }
    const c = node.lookup(PDFName.of("Contents"));
    const streams = c instanceof PDFArray ? Array.from({ length: c.size() }, (_, j) => c.lookup(j)) : [c];
    let bytes = 0;
    for (const s of streams) if (s instanceof PDFStream) bytes += s instanceof PDFRawStream ? s.contents.length : s.getContentsSize();
    if (bytes <= 6000) candidates.add(i);
  });
  if (!candidates.size) return [];
  const doc = await env.open();
  const OPS = env.ops;
  const painting = new Set<number>([
    OPS.showText, OPS.showSpacedText, OPS.nextLineShowText, OPS.nextLineSetSpacingShowText,
    OPS.paintImageXObject, OPS.paintImageXObjectRepeat, OPS.paintInlineImageXObject, OPS.paintImageMaskXObject,
    OPS.paintImageMaskXObjectGroup, OPS.paintImageMaskXObjectRepeat, OPS.paintFormXObjectBegin, OPS.shadingFill,
    OPS.fill, OPS.eoFill, OPS.fillStroke, OPS.eoFillStroke, OPS.closeFillStroke, OPS.closeEOFillStroke, OPS.stroke, OPS.closeStroke,
    OPS.paintSolidColorImageMask,
  ].filter((n) => typeof n === "number"));
  const blank: number[] = [];
  for (let i = 1; i <= doc.numPages; i++) {
    if (!candidates.has(i - 1)) continue;
    const page = await doc.getPage(i);
    if ((await page.getAnnotations()).length) continue;
    const list = await page.getOperatorList();
    if (!Array.from(list.fnArray).some((fn: number) => painting.has(fn))) blank.push(i - 1);
  }
  return blank;
}

/** Plain reason a size limit could not be met after every allowed step was tried. `how: "api"` words the way out as a
 *  request field (limit=extreme) instead of the site's "Go as far as" menu; everything else is the same text. */
export function whyNotReached(size: number, target: number, limit: CompressLimit, pages: number, how: "site" | "api" = "site"): string {
  const head = `Every step was tried and the smallest file is ${formatFileSize(size)}, above your ${formatFileSize(target)} target.`;
  if (limit === "balanced") return `${head} Only the Balanced steps were allowed (text stays selectable). ${how === "api" ? "Send limit=aggressive or limit=extreme" : 'Choose "Go as far as" Aggressive or Extreme'} to let pages become pictures.`;
  if (limit === "aggressive") return `${head} The Aggressive steps are finished. Going further means Extreme, which makes pages grey and then black and white. ${how === "api" ? "Send limit=extreme" : 'Choose "Go as far as" Extreme'} to try it.`;
  const per = pages > 0 ? ` With ${plural(pages, "page")}, the target leaves about ${formatFileSize(Math.floor(target / pages))} for each page, but the smallest this tool can store a page of this file at without making it unreadable is about ${formatFileSize(Math.floor(size / pages))}.` : "";
  return `${head} This included the Extreme steps (grey, then black and white), so there is nothing smaller to try.${per} Splitting the document into parts is the other way to fit a limit.`;
}

/** Reduces a PDF's file size.
 *  - light: tidies the file's structure; text, pictures and everything else stay byte-for-byte as they were.
 *  - medium / strong: also shrinks the pictures inside the PDF (balanced / smallest). Text stays text.
 *  - target: shrinks pictures as little as it can while fitting a size limit. Only with
 *    `allowPictures` does it fall back to turning whole pages into pictures, and only when that
 *    ends up smaller than the best picture-shrinking result.
 * Every result keeps the page sizes, the title, bookmarks, links and form fields (the same document is
 * saved), except the "pictures" fallback, which says so. */
export async function compressPdfBytes(
  original: Uint8Array,
  mode: CompressMode,
  env: LadderEnv,
  options: {
    /** The file's name, used as the title when the PDF has none of its own. */
    fileName?: string;
    targetBytes?: number;
    allowPictures?: boolean;
    /** For "size": the furthest step the search may take. Default "extreme". */
    limit?: CompressLimit;
    onProgress?: (message: string) => void;
    shouldStop?: () => boolean;
    /** Called as each level finishes, with a complete file, so the person can see every step and stop at any of them. */
    onLevel?: (checkpoint: LevelCheckpoint) => void;
    /** Aggressive / Extreme: keep going through the heavier levels even when the file is already about 98% smaller. */
    runAllLevels?: boolean;
  } = {}
): Promise<CompressResult> {
  const codec = env.codec;
  const fileSize = original.length;
  const hooks = { shouldStop: options.shouldStop };

  const source = await PDFDocument.load(original, { updateMetadata: false });
  const { images, totalStoredBytes } = findImages(source);
  const imageCount = images.length;
  // Big pictures that could not be handled (JPEG 2000, scanner formats, CMYK ...) are not "text".
  // Scanner pictures (JBIG2 masks, JPEG 2000 layers) are often small per page, so count every picture's bytes.
  const hasUnshrinkable = imageCount === 0 && totalStoredBytes >= Math.max(50 * 1024, fileSize * 0.2);
  const noPicturesReason: CompressUnchangedReason = hasUnshrinkable ? "unsupported-pictures" : "no-images";
  const meta = { title: source.getTitle(), author: source.getAuthor(), fallbackTitle: stemOfPdfName(options.fileName ?? "document.pdf") };

  const finish = async (bytes: Uint8Array): Promise<Uint8Array> => {
    // Metadata is written onto the result without touching anything else: real title and author kept
    // (placeholders replaced), Producer is FreeDailyPro.
    const doc = await PDFDocument.load(bytes, { updateMetadata: false });
    stampOutputMetadata(doc, meta);
    return doc.save({ useObjectStreams: true });
  };
  const structureOnly = async (): Promise<Uint8Array> => {
    const doc = await PDFDocument.load(original, { updateMetadata: false });
    stampOutputMetadata(doc, meta);
    return doc.save({ useObjectStreams: true });
  };
  const worthIt = (size: number) => size <= fileSize * (1 - MIN_SAVING);
  let levelNo = 1;
  let lastLevelSize = Infinity;
  /** Reports a finished level, but only when it really is smaller than the last one shown. */
  const reportLevel = (name: string, detail: string, bytes: Uint8Array) => {
    if (!worthIt(bytes.length) || bytes.length >= lastLevelSize * 0.995) return false;
    lastLevelSize = bytes.length;
    options.onLevel?.({ level: levelNo++, name, detail, bytes, size: bytes.length, turnsPagesToPictures: false });
    return true;
  };

  let effMode: CompressMode = mode;
  let effTarget = options.targetBytes;
  let tidyBytes: Uint8Array | undefined;
  // The four step-by-step modes all run the same ladder; they differ in the goal and in how far it may go.
  let ceiling: CompressLimit = "extreme";
  let noTarget = false; // Balanced / Aggressive / Extreme have no size goal: they run every step up to their limit
  if (mode === "balanced" || mode === "aggressive" || mode === "extreme" || mode === "size") {
    ceiling = mode === "size" ? (options.limit ?? "extreme") : mode;
    if (mode !== "size") { noTarget = true; effTarget = Math.max(1, Math.floor(fileSize * 0.02)); }
    effMode = "target";
  }
  if (mode === "auto") {
    // Default: aim for about half the size. Text and structure first; pictures only if that is not enough.
    options.onProgress?.("Tidying the file...");
    const tidy = await structureOnly();
    tidyBytes = tidy;
    reportLevel(TIDY_LEVEL.name, TIDY_LEVEL.detail, tidy);
    if (tidy.length <= fileSize * 0.5) return { status: "ok", bytes: tidy, size: tidy.length, method: "structure", imagesShrunk: 0, imageCount };
    effMode = "target";
    effTarget = Math.floor(fileSize * 0.5);
  }

  if (effMode === "medium" || effMode === "strong") {
    if (imageCount === 0) {
      options.onProgress?.("No pictures to shrink; tidying the file...");
      const bytes = await structureOnly();
      if (!worthIt(bytes.length)) return { status: "unchanged", reason: noPicturesReason, imageCount, pictureFallbackWouldHelp: !options.allowPictures };
      return { status: "ok", bytes, size: bytes.length, method: "structure", imagesShrunk: 0, imageCount };
    }
    const result = await recompressImages(original, effMode === "medium" ? BALANCED_SETTING : SMALLEST_SETTING, codec, {
      ...hooks,
      onProgress: (done, total) => options.onProgress?.(`Shrinking picture ${Math.min(done + 1, total)} of ${total}...`),
    });
    const bytes = await finish(result.bytes);
    if (!worthIt(bytes.length)) return { status: "unchanged", reason: "no-gain", imageCount, pictureFallbackWouldHelp: !options.allowPictures };
    return { status: "ok", bytes, size: bytes.length, method: "images", imagesShrunk: result.replaced, imageCount };
  }

  // Target size.
  const targetBytes = effTarget;
  if (!targetBytes || targetBytes <= 0) throw new Error("A valid target size is required for this compression mode");
  if (!noTarget && fileSize <= targetBytes) return { status: "unchanged", reason: "under-target", imageCount };
  const ok = (r: Extract<CompressResult, { status: "ok" }>): CompressResult => (noTarget ? { ...r, metTarget: undefined, targetBytes: undefined } : r);

  const goal = Math.floor(targetBytes * TARGET_MARGIN);
  // Level 1: tidy the structure. No rendering, no pictures touched: instant.
  const tidy = tidyBytes ?? await structureOnly();
  if (tidyBytes === undefined) reportLevel(TIDY_LEVEL.name, TIDY_LEVEL.detail, tidy);
  let best: { bytes: Uint8Array; method: "structure" | "images"; replaced: number } = { bytes: tidy, method: "structure", replaced: 0 };
  if (tidy.length <= targetBytes) return ok({ status: "ok", bytes: tidy, size: tidy.length, method: "structure", metTarget: true, targetBytes, imagesShrunk: 0, imageCount });

  // Level 2: pages that draw nothing are emptied, found without rendering anything. Later levels work on that file.
  let work: Uint8Array = original;
  try {
    options.onProgress?.("Looking for blank pages...");
    const blanks = await findBlankPagesIn(original, env);
    if (blanks.length) {
      const emptied = await finish(await emptyPages(original, blanks, undefined));
      if (reportLevel(BLANKS_LEVEL.name, BLANKS_LEVEL.detail, emptied)) {
        work = emptied;
        best = { bytes: emptied, method: "structure", replaced: 0 };
        if (emptied.length <= targetBytes) return ok({ status: "ok", bytes: emptied, size: emptied.length, method: "structure", metTarget: true, targetBytes, imagesShrunk: 0, imageCount });
      }
    }
  } catch (e) {
    if (e instanceof CompressStopped) throw e;
  }

  const newMode = mode === "balanced" || mode === "aggressive" || mode === "extreme" || mode === "size";
  const pagesAllowed = newMode ? ceiling !== "balanced" : !!options.allowPictures;
  /** Ends the run with the best file so far (used when a mode's limit is reached before any page is turned into a picture). */
  const stopNote = (kind: StopNote["kind"]): StopNote | undefined =>
    noTarget && ceiling !== "balanced" && levelNo > 1
      ? { kind, level: levelNo - 1, savedPercent: Math.floor((1 - best.bytes.length / fileSize) * 100), limit: ceiling }
      : undefined;
  const finishLadder = (kind?: StopNote["kind"]): CompressResult => {
    if (!worthIt(best.bytes.length)) return { status: "unchanged", reason: imageCount === 0 ? noPicturesReason : "no-gain", imageCount, pictureFallbackWouldHelp: !pagesAllowed, pictureFallbackTried: pagesAllowed };
    return ok({ status: "ok", bytes: best.bytes, size: best.bytes.length, method: best.method, metTarget: best.bytes.length <= targetBytes, targetBytes, imagesShrunk: best.replaced, imageCount, pictureFallbackWouldHelp: !pagesAllowed, pictureFallbackTried: pagesAllowed, stopNote: kind ? stopNote(kind) : undefined });
  };

  if (imageCount > 0) {
    // Level 3: one quick pass over the pictures inside at a balanced setting.
    options.onProgress?.("Shrinking the pictures inside...");
    const quick = await recompressImages(work, noTarget && ceiling === "balanced" ? BALANCED_140 : BALANCED_SETTING, codec, {
      ...hooks,
      onProgress: (done, total) => options.onProgress?.(`Shrinking picture ${Math.min(done + 1, total)} of ${total}...`),
    });
    const quickBytes = await finish(quick.bytes);
    // The quick pass is a level only when it does not over-shrink: if it lands well under the size asked for, a sharper fit exists, so the next level does that instead.
    const overShrunk = quickBytes.length < goal * 0.88;
    if (!overShrunk && reportLevel(PICS_QUICK_LEVEL.name, PICS_QUICK_LEVEL.detail, quickBytes) && quickBytes.length < best.bytes.length) best = { bytes: quickBytes, method: "images", replaced: quick.replaced };
    if (!noTarget && !overShrunk && best.bytes.length <= targetBytes) return ok({ status: "ok", bytes: best.bytes, size: best.bytes.length, method: "images", metTarget: true, targetBytes, imagesShrunk: best.replaced, imageCount });

    // Level 4: measure every picture and shrink the ones that lose the least quality per byte saved, until the size fits.
    const smart = await recompressSmart(work, goal, codec, {
      ...hooks,
      onPass: (pass) => options.onProgress?.(pass === 1 ? "Measuring each picture..." : "Choosing the best setting for each picture..."),
      onProgress: (done, total) => options.onProgress?.(`Picture ${Math.min(done + 1, total)} of ${total}...`),
    });
    const fit = smart ?? await recompressToFit(work, goal, codec, {
      ...hooks,
      onPass: (pass, passes) => options.onProgress?.(`Finding the sharpest result that fits: pass ${pass} of ${passes}...`),
      onProgress: (done, total) => options.onProgress?.(`Shrinking picture ${Math.min(done + 1, total)} of ${total}...`),
    });
    const fitBytes = await finish(fit.bytes);
    if (reportLevel(PICS_FIT_LEVEL.name, PICS_FIT_LEVEL.detail, fitBytes) && fitBytes.length < best.bytes.length) best = { bytes: fitBytes, method: "images", replaced: fit.replaced };
  }

  if (noTarget && ceiling === "balanced") return finishLadder(); // Balanced ends here: every step that keeps text selectable has run
  if (best.bytes.length <= targetBytes) {
    // Aggressive / Extreme: the pictures alone already got the file to about 2%. Say so instead of silently handing back the same file.
    if (noTarget && !options.runAllLevels) return finishLadder("already-small");
    if (!noTarget) return ok({ status: "ok", bytes: best.bytes, size: best.bytes.length, method: best.method, metTarget: true, targetBytes, imagesShrunk: best.replaced, imageCount });
  }

  if (pagesAllowed) {
    const pics = await compressPagesInLevelsWith(env, {
      base: best.bytes, goal, mustBeat: fileSize * (1 - MIN_SAVING), firstLevel: levelNo, extreme: ceiling === "extreme",
      stamp: (doc) => stampOutputMetadata(doc, meta),
      onLevel: (cp) => { levelNo = cp.level + 1; lastLevelSize = cp.size; options.onLevel?.(cp); },
      onProgress: options.onProgress, shouldStop: options.shouldStop,
    });
    if (pics && pics.size < best.bytes.length) {
      return ok({ status: "ok", bytes: pics.bytes, size: pics.size, method: "pictures", metTarget: pics.size <= targetBytes, targetBytes, imagesShrunk: 0, imageCount, pagesConverted: pics.pagesConverted, pagesKept: pics.pagesKept });
    }
  }

  // The target could not be reached. If nothing even made the file smaller, say so, and still offer the last step.
  return finishLadder("no-smaller");
}


